// TUT-334 — o vocabulário da tela de Contas a Pagar, e o espelho da lista
// para a demonstração (que roda sem servidor).
//
// As faixas, a ordem, a busca e os totais são a MESMA regra de
// `apps/api/src/domain/accounts-payable-list.ts`. O web é pacote separado e
// não importa da API — a cópia é o preço do modo demonstração, e o
// `contas-a-pagar.test.ts` prova os mesmos casos dos dois lados.
import type {
  AccountsPayable,
  AccountsPayableListQuery,
  AccountsPayableListResponse,
  FaixaDeContaAPagar,
} from "@/lib/types";

export const FAIXAS_DE_CONTAS_A_PAGAR: readonly {
  chave: FaixaDeContaAPagar;
  rotulo: string;
  tom?: "primary" | "success" | "warning" | "critical";
}[] = [
  { chave: "vencidas", rotulo: "Vencidas", tom: "critical" },
  { chave: "vencem_7_dias", rotulo: "Vencem em 7 dias", tom: "warning" },
  { chave: "a_vencer", rotulo: "A vencer", tom: "primary" },
  { chave: "sem_vencimento", rotulo: "Sem vencimento" },
  { chave: "aguardando_documentos", rotulo: "Aguardando documentos" },
  { chave: "pagas", rotulo: "Pagas", tom: "success" },
  { chave: "canceladas", rotulo: "Canceladas" },
];

/**
 * As faixas que viram CARTÃO no topo: as seis que organizam o pagamento. A
 * sétima, Canceladas, cabe na barra de filtros — medido em 1440: sete cartões
 * de 11rem não cabem numa linha, e o cartão sozinho na segunda linha era a
 * faixa que ninguém consulta no dia a dia.
 */
export const FAIXAS_NOS_CARTOES = FAIXAS_DE_CONTAS_A_PAGAR.filter((item) => item.chave !== "canceladas");

/** A ordem das faixas no domínio (a da API), usada por `situacao`. */
const ORDEM_DAS_FAIXAS: readonly FaixaDeContaAPagar[] = [
  "aguardando_documentos",
  "sem_vencimento",
  "vencidas",
  "vencem_7_dias",
  "a_vencer",
  "pagas",
  "canceladas",
];

export function isFaixaDeContaAPagar(valor: unknown): valor is FaixaDeContaAPagar {
  return typeof valor === "string" && (ORDEM_DAS_FAIXAS as readonly string[]).includes(valor);
}

export function rotuloDaFaixa(faixa: FaixaDeContaAPagar): string {
  return FAIXAS_DE_CONTAS_A_PAGAR.find((item) => item.chave === faixa)?.rotulo ?? faixa;
}

export function dinheiro(cents: number): string {
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(cents / 100);
}

/** `AAAA-MM-DD` → `28/09/2026`, sem passar por fuso (é dia de calendário). */
export function diaPorExtenso(dia: string): string {
  const [ano, mes, dd] = dia.split("-");
  return `${dd}/${mes}/${ano}`;
}

/** "Hoje" como dia de calendário local, `AAAA-MM-DD`. */
export function hojeLocal(agora = new Date()): string {
  const mes = String(agora.getMonth() + 1).padStart(2, "0");
  const dia = String(agora.getDate()).padStart(2, "0");
  return `${agora.getFullYear()}-${mes}-${dia}`;
}

function somarDias(dia: string, dias: number): string {
  const [ano = 0, mes = 1, dd = 1] = dia.split("-").map(Number);
  return new Date(Date.UTC(ano, mes - 1, dd + dias)).toISOString().slice(0, 10);
}

export function faixaDaConta(
  conta: Pick<AccountsPayable, "status" | "dueDate">,
  hoje: string,
): FaixaDeContaAPagar {
  if (conta.status === "AWAITING_DELIVERABLES") return "aguardando_documentos";
  if (conta.status === "PAID") return "pagas";
  if (conta.status === "CANCELLED") return "canceladas";
  if (!conta.dueDate) return "sem_vencimento";
  if (conta.dueDate < hoje) return "vencidas";
  if (conta.dueDate <= somarDias(hoje, 7)) return "vencem_7_dias";
  return "a_vencer";
}

function normalizar(texto: string): string {
  return texto.normalize("NFD").replace(/\p{Diacritic}/gu, "").toLocaleLowerCase("pt-BR");
}

function comparar(a: string | number | null, b: string | number | null, direcao: "asc" | "desc" | undefined): number {
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  const sinal = direcao === "desc" ? -1 : 1;
  if (typeof a === "number" && typeof b === "number") return sinal * (a - b);
  return sinal * String(a).localeCompare(String(b), "pt-BR", { numeric: true, sensitivity: "base" });
}

/** O espelho de `montarListaDeContasAPagar` (API), para a demonstração. */
export function montarListaDeContasAPagar(
  todas: readonly AccountsPayable[],
  filtros: AccountsPayableListQuery & { includeClosed?: boolean; hoje: string },
): AccountsPayableListResponse {
  const faixaDe = (conta: AccountsPayable) => faixaDaConta(conta, filtros.hoje);
  const busca = filtros.search?.trim() ? normalizar(filtros.search.trim()) : null;
  const doProcesso = filtros.processo?.trim() ? normalizar(filtros.processo.trim()) : null;
  const representantes = filtros.representante?.length ? new Set(filtros.representante) : null;
  const buscadas = todas.filter((conta) => {
    // TUT-585 — o favorecido pode ser fornecedor; o processo e a descrição são
    // opcionais na conta avulsa, então a busca olha os três sem quebrar no NULL.
    if (busca && !normalizar(conta.payeeName).includes(busca) && !normalizar(conta.processNumber ?? "").includes(busca) && !normalizar(conta.description ?? "").includes(busca)) return false;
    if (doProcesso && !normalizar(conta.processNumber ?? "").includes(doProcesso)) return false;
    if (representantes && !(conta.representativeId && representantes.has(conta.representativeId))) return false;
    if (filtros.de || filtros.ate) {
      if (!conta.dueDate) return false;
      if (filtros.de && conta.dueDate < filtros.de) return false;
      if (filtros.ate && conta.dueDate > filtros.ate) return false;
    }
    if (filtros.valorMin !== undefined && conta.contractedAmountCents < filtros.valorMin) return false;
    if (filtros.valorMax !== undefined && conta.contractedAmountCents > filtros.valorMax) return false;
    return true;
  });
  const faixas = Object.fromEntries(
    ORDEM_DAS_FAIXAS.map((faixa) => [faixa, { quantidade: 0, totalCents: 0 }]),
  ) as AccountsPayableListResponse["faixas"];
  for (const conta of buscadas) {
    faixas[faixaDe(conta)].quantidade += 1;
    faixas[faixaDe(conta)].totalCents += conta.contractedAmountCents;
  }
  const recortadas = buscadas.filter((conta) => {
    const faixa = faixaDe(conta);
    if (filtros.faixa?.length) return filtros.faixa.includes(faixa);
    if (filtros.includeClosed) return true;
    return faixa !== "pagas" && faixa !== "canceladas";
  });
  const valor: Record<string, (conta: AccountsPayable) => string | number | null> = {
    representante: (conta) => conta.payeeName,
    processo: (conta) => conta.processNumber,
    vencimento: (conta) => conta.dueDate,
    valor: (conta) => conta.contractedAmountCents,
    situacao: (conta) => ORDEM_DAS_FAIXAS.indexOf(faixaDe(conta)),
  };
  const acessor = filtros.sort ? valor[filtros.sort] : undefined;
  const ordenadas = [...recortadas].sort((a, b) => {
    const principal = acessor
      ? comparar(acessor(a), acessor(b), filtros.dir)
      : comparar(a.dueDate, b.dueDate, "asc") || b.createdAt.localeCompare(a.createdAt);
    return principal || a.id.localeCompare(b.id);
  });
  const porId = new Map<string, string>();
  // O filtro "Representante" lista só quem é assistente externo (TUT-585): o fornecedor não tem `representativeId`.
  for (const conta of todas) {
    if (conta.representativeId && !porId.has(conta.representativeId)) porId.set(conta.representativeId, conta.payeeName);
  }
  const opcoes = [...porId]
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name, "pt-BR") || a.id.localeCompare(b.id));
  const total = ordenadas.length;
  if (!filtros.page || !filtros.pageSize) {
    return { items: ordenadas, page: 1, pageSize: total || 1, total, totalPages: 1, faixas, representantes: opcoes };
  }
  const totalPages = Math.max(1, Math.ceil(total / filtros.pageSize));
  const page = Math.min(Math.max(1, filtros.page), totalPages);
  return {
    items: ordenadas.slice((page - 1) * filtros.pageSize, page * filtros.pageSize),
    page,
    pageSize: filtros.pageSize,
    total,
    totalPages,
    faixas,
    representantes: opcoes,
  };
}
